import { CLIENT_EVENTS, type ServerToClientEvents } from '@minigame-race/contracts';
import { io, type Socket } from 'socket.io-client';

/**
 * Typed socket wrapper. Both directions are typed from the shared contracts
 * package, so a payload shape can never drift between client and server.
 */

interface ClientToServerEvents {
  [CLIENT_EVENTS.queueJoin]: () => void;
  [CLIENT_EVENTS.queueLeave]: () => void;
  [CLIENT_EVENTS.roomCreate]: () => void;
  [CLIENT_EVENTS.roomJoin]: (p: { code: string }) => void;
  [CLIENT_EVENTS.roomCancel]: () => void;
  [CLIENT_EVENTS.matchReady]: (p: { matchId: string }) => void;
  [CLIENT_EVENTS.matchLeave]: (p: { matchId: string }) => void;
  [CLIENT_EVENTS.minigameEvent]: (p: {
    matchId: string;
    segmentIndex: number;
    event: unknown;
  }) => void;
  [CLIENT_EVENTS.reaction]: (p: { matchId: string; reaction: string }) => void;
  [CLIENT_EVENTS.rematchOffer]: (p: { matchId: string }) => void;
  [CLIENT_EVENTS.rematchCancel]: (p: { matchId: string }) => void;
}

export type GameSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

/**
 * Where the game server lives.
 *
 * Defaults to the SAME HOST the page was served from, on port 3001 — not to
 * `localhost`. Hardcoding localhost breaks every device that isn't this
 * machine: a phone loading http://192.168.1.8:5173 would fetch its own
 * :3001 and fail. Deriving from `window.location.hostname` means the LAN, a
 * tunnel, and localhost all work with no configuration.
 *
 * Override with VITE_SERVER_URL when the API is not on the same host.
 */
const SERVER_PORT = 3001;

function defaultServerUrl(): string {
  if (typeof window === 'undefined') return `http://localhost:${SERVER_PORT}`;
  const { protocol, hostname } = window.location;
  return `${protocol}//${hostname}:${SERVER_PORT}`;
}

const SERVER_URL = (import.meta.env['VITE_SERVER_URL'] as string | undefined) ?? defaultServerUrl();

export function connectSocket(token: string): GameSocket {
  return io(SERVER_URL, {
    auth: { token },
    transports: ['websocket'],
    // The server's reconnect window is ~10s. Retry fast and often enough to
    // land inside it, rather than backing off past the forfeit deadline.
    reconnection: true,
    reconnectionAttempts: 12,
    reconnectionDelay: 400,
    reconnectionDelayMax: 1500,
    timeout: 8000,
  });
}

export const serverUrl = SERVER_URL;
